"use client";

import React, { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import {
    Upload,
    X,
    Image as ImageIcon,
    Video,
    Check,
    AlertCircle,
    Loader2,
    Camera,
    Film,
} from 'lucide-react';
import { apiService } from "@/services/api";

interface UploadedFile {
    id: string;
    file: File;
    preview?: string;
    type: 'image' | 'video';
    status: 'pending' | 'uploading' | 'success' | 'error';
    progress: number;
    error?: string;
}

const PhotoVideoUpload: React.FC = () => {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploaderName, setUploaderName] = useState("");
    const [uploadMessage, setUploadMessage] = useState("");
    const [category, setCategory] = useState<string>('other'); // ✅ ÉTAT CATÉGORIE
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
    const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];

    const validateFile = (file: File): string | null => {
        if (file.size > MAX_FILE_SIZE) {
            return "Le fichier est trop volumineux (max 100MB)";
        }

        const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type);
        const isVideo = ACCEPTED_VIDEO_TYPES.includes(file.type);

        if (!isImage && !isVideo) {
            return "Format non supporté. Utilisez JPG, PNG, GIF, MP4 ou MOV";
        }

        return null;
    };

    const handleFiles = useCallback((newFiles: FileList | File[]) => {
        const fileArray = Array.from(newFiles);

        fileArray.forEach(file => {
            const error = validateFile(file);

            if (error) {
                alert(error);
                return;
            }

            const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type);
            const id = `${Date.now()}-${Math.random()}`;

            const uploadedFile: UploadedFile = {
                id,
                file,
                type: isImage ? 'image' : 'video',
                status: 'pending',
                progress: 0,
            };

            // Créer une prévisualisation pour les images
            if (isImage) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setFiles(prev => prev.map(f =>
                        f.id === id ? { ...f, preview: e.target?.result as string } : f
                    ));
                };
                reader.readAsDataURL(file);
            }

            setFiles(prev => [...prev, uploadedFile]);
        });
    }, []);

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles.length > 0) {
            handleFiles(droppedFiles);
        }
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(e.target.files);
        }
    };

    const removeFile = (id: string) => {
        setFiles(prev => prev.filter(f => f.id !== id));
    };

    const uploadFile = async (fileData: UploadedFile) => {
        try {
            setFiles(prev => prev.map(f =>
                f.id === fileData.id ? { ...f, status: 'uploading', progress: 0 } : f
            ));

            const formData = new FormData();
            formData.append('file', fileData.file);
            formData.append('uploader_name', uploaderName);
            formData.append('message', uploadMessage);
            formData.append('type', fileData.type);
            formData.append('category', category); // ✅ AJOUTER CATÉGORIE

            // Simuler la progression (à adapter selon votre API)
            const progressInterval = setInterval(() => {
                setFiles(prev => prev.map(f => {
                    if (f.id === fileData.id && f.progress < 90) {
                        return { ...f, progress: f.progress + 10 };
                    }
                    return f;
                }));
            }, 200);

            const response = await apiService.uploadMedia(formData, (progress) => {
                setFiles(prev => prev.map(f =>
                    f.id === fileData.id ? { ...f, progress } : f
                ));
            });

            clearInterval(progressInterval);

            if (response.success) {
                setFiles(prev => prev.map(f =>
                    f.id === fileData.id ? { ...f, status: 'success', progress: 100 } : f
                ));
            } else {
                throw new Error(response.message || 'Erreur lors de l\'upload');
            }
        } catch (error) {
            setFiles(prev => prev.map(f =>
                f.id === fileData.id ? {
                    ...f,
                    status: 'error',
                    error: error instanceof Error ? error.message : 'Erreur inconnue'
                } : f
            ));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!uploaderName.trim()) {
            alert("Veuillez entrer votre nom");
            return;
        }

        if (files.length === 0) {
            alert("Veuillez sélectionner au moins un fichier");
            return;
        }

        const pendingFiles = files.filter(f => f.status === 'pending');

        for (const file of pendingFiles) {
            await uploadFile(file);
        }

        const allSuccess = files.every(f => f.status === 'success');
        if (allSuccess) {
            setShowSuccessModal(true);
            setTimeout(() => {
                resetForm();
            }, 2000);
        }
    };

    const resetForm = () => {
        setFiles([]);
        setUploaderName("");
        setUploadMessage("");
        setCategory('other'); // ✅ RESET CATÉGORIE
        setShowSuccessModal(false);
    };

    const pendingFilesCount = files.filter(f => f.status === 'pending').length;
    const successFilesCount = files.filter(f => f.status === 'success').length;

    return (
        <section className="py-16 md:py-24 bg-gradient-to-br from-bordeaux-50 via-white to-gold-50 relative">
            {/* En-tête */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-12"
            >
                <h2 className="heading-secondary mb-4">
                    Partagez vos souvenirs
                </h2>
                <div className="w-32 h-1 bg-gradient-to-r from-bordeaux-600 to-gold-600 mx-auto mb-6 rounded-full"></div>
                <p className="text-xl text-bordeaux-600 max-w-2xl mx-auto leading-relaxed">
                    Le mariage était magique, et nous aimerions revivre ces moments à travers vos yeux !
                    <br />
                    Partagez vos photos et vidéos avec nous.
                </p>
            </motion.div>

            {/* Formulaire principal */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="max-w-4xl mx-auto px-4"
            >
                <form onSubmit={handleSubmit} className="bg-white/95 backdrop-blur-md shadow-2xl border border-white/20 rounded-3xl overflow-hidden">
                    <div className="p-8 md:p-12">
                        {/* Zone de drop */}
                        <div
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className={`relative border-3 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer ${
                                isDragging
                                    ? 'border-bordeaux-500 bg-bordeaux-50 scale-105'
                                    : 'border-bordeaux-200 bg-bordeaux-50/30 hover:border-bordeaux-400 hover:bg-bordeaux-50'
                            }`}
                            onClick={() => window.open("https://photos.melevent.fr/", "_blank")}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept={[...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES].join(',')}
                                onChange={handleFileInput}
                                className="hidden"
                            />

                            <div className="flex flex-col items-center space-y-4">
                                <div className="flex space-x-4">
                                    <div className="w-16 h-16 bg-bordeaux-100 rounded-full flex items-center justify-center">
                                        <Camera className="w-8 h-8 text-bordeaux-600" />
                                    </div>
                                    <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center">
                                        <Film className="w-8 h-8 text-gold-600" />
                                    </div>
                                </div>

                                <div>
                                    <p className="text-xl font-semibold text-bordeaux-700 mb-2">
                                        Déposez vos fichiers ici
                                    </p>
                                    <p className="text-bordeaux-500">
                                        ou cliquez pour sélectionner
                                    </p>
                                </div>

                                <p className="text-sm text-bordeaux-400">
                                    Photos (JPG, PNG, GIF) et Vidéos (MP4, MOV) - Max 100MB par fichier
                                </p>
                            </div>
                        </div>

                        {/* Liste des fichiers */}
                        <AnimatePresence>
                            {files.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="mt-8 space-y-4"
                                >
                                    <h4 className="font-semibold text-bordeaux-700 text-lg mb-4">
                                        Fichiers sélectionnés ({files.length})
                                    </h4>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {files.map((fileData) => (
                                            <motion.div
                                                key={fileData.id}
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.9 }}
                                                className="bg-white border-2 border-bordeaux-100 rounded-xl p-4 relative"
                                            >
                                                {/* Bouton supprimer */}
                                                {fileData.status === 'pending' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFile(fileData.id)}
                                                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors z-10"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}

                                                <div className="flex items-start space-x-3">
                                                    {/* Prévisualisation */}
                                                    <div className="flex-shrink-0 w-16 h-16 bg-bordeaux-50 rounded-lg flex items-center justify-center overflow-hidden">
                                                        {fileData.type === 'image' && fileData.preview ? (
                                                            <img
                                                                src={fileData.preview}
                                                                alt="Preview"
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : fileData.type === 'image' ? (
                                                            <ImageIcon className="w-8 h-8 text-bordeaux-400" />
                                                        ) : (
                                                            <Video className="w-8 h-8 text-bordeaux-400" />
                                                        )}
                                                    </div>

                                                    {/* Informations */}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-bordeaux-700 truncate">
                                                            {fileData.file.name}
                                                        </p>
                                                        <p className="text-sm text-bordeaux-400">
                                                            {(fileData.file.size / (1024 * 1024)).toFixed(2)} MB
                                                        </p>

                                                        {/* Statut */}
                                                        <div className="mt-2">
                                                            {fileData.status === 'pending' && (
                                                                <span className="text-xs text-gray-500 flex items-center">
                                                                    <Upload className="w-3 h-3 mr-1" />
                                                                    En attente
                                                                </span>
                                                            )}

                                                            {fileData.status === 'uploading' && (
                                                                <div className="space-y-1">
                                                                    <div className="flex items-center text-xs text-blue-600">
                                                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                                        Upload en cours... {fileData.progress}%
                                                                    </div>
                                                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                                        <div
                                                                            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                                                            style={{ width: `${fileData.progress}%` }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {fileData.status === 'success' && (
                                                                <span className="text-xs text-green-600 flex items-center">
                                                                    <Check className="w-3 h-3 mr-1" />
                                                                    Envoyé avec succès
                                                                </span>
                                                            )}

                                                            {fileData.status === 'error' && (
                                                                <span className="text-xs text-red-600 flex items-center">
                                                                    <AlertCircle className="w-3 h-3 mr-1" />
                                                                    {fileData.error || 'Erreur'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Bouton de soumission */}
                        {files.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="mt-8 text-center"
                            >
                                <button
                                    type="submit"
                                    disabled={pendingFilesCount === 0 || !uploaderName.trim()}
                                    className={`px-8 py-4 text-lg font-semibold rounded-2xl transition-all duration-200 shadow-lg ${
                                        pendingFilesCount > 0 && uploaderName.trim()
                                            ? 'bg-gradient-to-r from-bordeaux-600 to-bordeaux-700 text-white hover:from-bordeaux-700 hover:to-bordeaux-800 hover:shadow-xl transform hover:scale-105'
                                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    }`}
                                >
                                    {pendingFilesCount > 0
                                        ? `Envoyer ${pendingFilesCount} fichier${pendingFilesCount > 1 ? 's' : ''}`
                                        : successFilesCount > 0
                                            ? '✅ Tous les fichiers sont envoyés'
                                            : 'Envoyer les fichiers'}
                                </button>
                            </motion.div>
                        )}
                    </div>
                </form>
            </motion.div>

            {/* Modal de succès */}
            <AnimatePresence>
                {showSuccessModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                        onClick={() => setShowSuccessModal(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-8 h-8 text-green-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-bordeaux-700 mb-4">
                                Merci !
                            </h3>
                            <p className="text-bordeaux-600 mb-6">
                                Vos fichiers ont bien été envoyés.
                                <br />
                                Nous avons hâte de les découvrir !
                            </p>
                            <button
                                onClick={() => setShowSuccessModal(false)}
                                className="btn-primary w-full"
                            >
                                Fermer
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default PhotoVideoUpload;
